"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL || "https://checkinoutpro.com";

export function ContactForm() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to send message");

      toast({
        title: "Message Sent Successfully!",
        description: "We'll get back to you as soon as possible.",
        variant: "success",
      });

      setFormData({ email: "", subject: "", message: "" });
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto backdrop-blur-2xl border border-white/30 dark:border-gray-800/50 bg-white/70 dark:bg-gray-900/50 shadow-2xl rounded-3xl">
      <CardHeader className="text-center space-y-1 pb-4">
        <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">
          Get in Touch
        </CardTitle>
        <CardDescription className="text-gray-600 dark:text-gray-400">
          We'd love to hear from you. Fill out the form below.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div className="grid gap-2">
            <Label htmlFor="email" className="text-gray-800 dark:text-gray-200">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-white/80 dark:bg-gray-800/70 focus:ring-emerald-500"
            />
          </div>

          {/* Subject */}
          <div className="grid gap-2">
            <Label
              htmlFor="subject"
              className="text-gray-800 dark:text-gray-200"
            >
              Subject
            </Label>
            <Input
              id="subject"
              placeholder="Enter your subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="bg-white/80 dark:bg-gray-800/70 focus:ring-emerald-500"
            />
          </div>

          {/* Message */}
          <div className="grid gap-2">
            <Label
              htmlFor="message"
              className="text-gray-800 dark:text-gray-200"
            >
              Message
            </Label>
            <Textarea
              id="message"
              placeholder="Write your message..."
              value={formData.message}
              onChange={handleChange}
              required
              className="min-h-[150px] resize-y bg-white/80 dark:bg-gray-800/70 focus:ring-emerald-500"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 shadow-lg active:scale-95 transition-all"
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
